// Spec §67 home page: shows the signed-in user + a "Recent reports"
// list (max 5) and links to the workspace + create-report flow.
//
// Phase 1.5: the recent-reports loader surfaces an explicit error state
// with a retry button so the user can distinguish "no reports yet"
// from "the request failed". No retry/backoff abstraction — just a
// single click that re-issues the same query.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import { REPORT_STATUS_LABEL, REPORT_STATUS_TONE, formatRelative } from '@/utils/format';
import type { ReportJson } from '@/api/types';

export function HomeScreen() {
  const { session, getToken } = useAuth();
  const [recent, setRecent] = useState<ReportJson[]>([]);
  const [recentStatus, setRecentStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const loadRecent = useCallback(async () => {
    setRecentStatus('loading');
    try {
      const { rows } = await reportsApi.list(getToken, { limit: 5 });
      setRecent(rows);
      setRecentStatus('success');
    } catch (e) {
      // Surface as a user-visible error state rather than the previous
      // silent `void e`. The raw API error is intentionally not shown;
      // we only need to know *that* it failed, not the message.
      if (e instanceof ApiException) {
        // Intentionally unused — message is intentionally suppressed in
        // favour of a friendlier generic copy above.
        void e;
      }
      setRecent([]);
      setRecentStatus('error');
    }
  }, [getToken]);

  useEffect(() => {
    void loadRecent();
  }, [loadRecent]);

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          Welcome back{session?.username ? `, ${session.username}` : ''}
        </h2>
        <p className="muted" style={{ marginTop: 0 }}>
          {session?.email}
        </p>
        {/* Primary CTA — full width, prominent */}
        <Link to="/reports/new" className="btn btn--primary home-cta-btn">
          + New Field Report
        </Link>
        {/* Secondary actions */}
        <div className="row home-secondary-actions" style={{ marginTop: 12 }}>
          <Link to="/workspace" className="btn">
            Browse workspace
          </Link>
          <Link to="/reports" className="btn">
            All reports
          </Link>
        </div>
      </div>

      <div className="card">
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}
        >
          <h3 style={{ margin: 0 }}>Recent reports</h3>
          <Link to="/reports" className="muted" style={{ fontSize: 14 }}>
            View all
          </Link>
        </div>
        {recentStatus === 'loading' ? (
          <div className="empty" data-testid="recent-loading">
            Loading recent reports…
          </div>
        ) : recentStatus === 'error' ? (
          <div className="stack" data-testid="recent-error" style={{ alignItems: 'stretch' }}>
            <div className="error-banner">Unable to load recent reports. Please try again.</div>
            <button type="button" className="btn" onClick={() => void loadRecent()}>
              Retry
            </button>
          </div>
        ) : recent.length === 0 ? (
          <div className="empty">
            <p>You have not created any reports yet.</p>
            <Link to="/reports/new" className="btn btn--primary">
              Start your first report
            </Link>
          </div>
        ) : (
          <div className="list">
            {recent.map((row) => (
              <Link key={row.id} to={`/reports/${row.id}`} className="list__item">
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">
                    {row.title.trim() === '' ? '(Untitled)' : row.title}
                  </div>
                  <div className="list__item-subtitle">
                    <span className={REPORT_STATUS_TONE[row.status]}>
                      {REPORT_STATUS_LABEL[row.status]}
                    </span>
                    <span>· {formatRelative(row.updated_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
